import { FIELD_WIDTH } from '@fox/atoms/contracts/field';
import { Button } from '@fox/atoms/src/button/index';
import { Divider } from '@fox/atoms/src/divider/index';
import { IconButton } from '@fox/atoms/src/icon-button/index';
import { Icon } from '@fox/atoms/src/icon/index';
import { usePopoverContext } from '@fox/atoms/src/popover/index';
import { Typography } from '@fox/atoms/src/typography/index';
import { strIsNullOrEmpty } from '@fox/atoms/utils/string';
import { getExtension } from '@fox/extensions/src/extension-context';
import { FlexColumnSpaced, FlexRowSpaced, VerticalInset } from '@fox/layout/src/flex';
import { Padded } from '@fox/layout/src/style';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { useAutoDataGridContext } from '../../auto-data-grid/context';
import { FilterTypes } from '../../auto-data-grid/types';
import { FilterOperatorProps } from '../../contracts/FilterOperatorProps';
import { FilterField } from './field';
import { FilterComponent } from './filterComponent';
import { FilterModifier } from './modifier';
import { FilterConditionItemProps, LocalStateFilterObject, QueryFilter } from './types';

export const PopoverFilterConditionsGrid = styled.div<{ $columnsIsArray: boolean }>`
	display: grid;
	gap: 1rem;
	grid-template-columns: ${({ $columnsIsArray }) =>
		$columnsIsArray ? `3rem ${FIELD_WIDTH} repeat(2, ${FIELD_WIDTH}) 3rem` : `repeat(2, ${FIELD_WIDTH}) 3rem`};
	column-gap: 1rem;
	align-items: flex-start;
	width: 100%;
`;

export function FilterConditionItem({
	filter,
	totalFilterCount,
	index,
	operator,
	availableColumns,
	onRemove,
	onAddNewFilter,
	onValueChange,
	filterOperatorsExt,
	appliedFilters,
	setAppliedFilters,
	onModifierChange
}: FilterConditionItemProps) {
	const isFirst = index === 0;
	const isLast = totalFilterCount - 1 === index;
	const hasOtherFilters = totalFilterCount > 1;
	const columnsIsArray = Array.isArray(availableColumns);
	return (
		<div>
			<FlexRowSpaced>
				<PopoverFilterConditionsGrid $columnsIsArray={columnsIsArray}>
					{columnsIsArray ? (
						<>
							<div style={{ paddingTop: '0.5rem' }}>
								<Typography variant="body1">{isFirst ? 'Where' : 'And'}</Typography>
							</div>
							<FilterField
								availableColumns={availableColumns}
								filter={filter}
								filterOperatorsExt={filterOperatorsExt}
								index={index}
								appliedFilters={appliedFilters}
								setAppliedFilters={setAppliedFilters}
							/>
						</>
					) : null}
					<FilterModifier filter={filter} operator={operator} onChange={e => onModifierChange(e, index)} />
					<FilterComponent
						filter={filter}
						editFilter={e => onValueChange(e, index)}
						filterParams={
							Array.isArray(availableColumns)
								? availableColumns.find(c => c.dataIndex === filter.dataIndex)?.filterParams
								: availableColumns?.filterParams
						}
					/>
					{hasOtherFilters ? (
						<IconButton
							iconName="Close"
							title="Remove"
							onClick={() => {
								onRemove(index);
							}}
						/>
					) : (
						<div></div>
					)}
				</PopoverFilterConditionsGrid>
			</FlexRowSpaced>
			{isLast ? (
				<VerticalInset>
					<Button style={{ color: '#272727' }} onClick={() => onAddNewFilter()}>
						<Icon iconName={'Add'} />
						Add a new filter
					</Button>
				</VerticalInset>
			) : null}
		</div>
	);
}

export function FilterPopover(props: { columns: ColumnFilter[] | ColumnFilter }) {
	const context = useAutoDataGridContext();
	const { closePopover } = usePopoverContext();

	if (!context) {
		throw new Error('FilterPopover must be used within AutoDataGridContext');
	}

	const { filters, setFilters } = context;

	return <FilterEditor columns={props.columns} filters={filters} onFiltersChange={setFilters} onClose={() => closePopover(true)} />;
}

export type ColumnFilter = {
	/** Specifies the title for this column. If not provided, the column will not be filterable as there is nothing to display. */
	title?: ReactNode;
	dataIndex: string;
	/** Specifies the filter type applicable to this column. If not set, this column will not be treated as filterable. */
	filterType?: FilterTypes;

	filterParams?: unknown;
};

/**
 * Standalone filter editor component that doesn't depend on any context.
 * Use this when you need filter functionality without the AutoDataGrid or Popover context.
 */
export function FilterEditor(props: {
	columns: ColumnFilter[] | ColumnFilter;
	filters: QueryFilter[];
	onFiltersChange: (filters: QueryFilter[]) => void;
	onClose?: () => void;
	showCloseButton?: boolean;
}) {
	const { columns, filters, onFiltersChange, onClose, showCloseButton = true } = props;

	let filterType: string | undefined;
	let dataIndex: string | undefined;
	let columnFilters: QueryFilter[];
	let otherFilters: QueryFilter[] | null = null;

	if (!Array.isArray(columns)) {
		filterType = columns.filterType;
		dataIndex = columns.dataIndex;
		columnFilters = filters.filter(el => el.dataIndex === dataIndex);
		otherFilters = filters.filter(el => el.dataIndex !== dataIndex);
	} else {
		columnFilters = filters;
	}

	const safeFilterOperatorsExt = (filterType: string | undefined): FilterOperatorProps => {
		const defaultOperator: FilterOperatorProps = {
			filterConstraints: [],
			humanNames: {},
			icon: 'FilterAlt'
		};
		return filterType ? (getExtension<FilterOperatorProps>(`column-operators-${filterType}`) ?? defaultOperator) : defaultOperator;
	};

	const defaulFilter = filterType ? getExtension<FilterOperatorProps>(`column-operators-${filterType}`)?.filterConstraints?.[0] : undefined;

	let currentFilters: LocalStateFilterObject[] = [];

	if (!Array.isArray(columns)) {
		if (columnFilters.length === 0) {
			currentFilters = [{ dataIndex: dataIndex ?? '', filterType: filterType ?? '', constrT: defaulFilter ?? '', constV: '' }];
		} else {
			currentFilters = columnFilters?.map(f => {
				const parsed = JSON.parse(f.filterValue) as Record<string, unknown>;
				const [key, value] = Object.entries(parsed)[0];
				return { dataIndex: f.dataIndex, filterType: f.filterType, constrT: key, constV: value as string };
			});
		}
	} else {
		if (filters.length === 0) {
			currentFilters = [{ dataIndex: '', filterType: '', constrT: '', constV: '' }];
		} else if (filters.length > 0) {
			currentFilters = filters?.map(f => {
				const parsed = JSON.parse(f.filterValue) as Record<string, unknown>;
				const [key, value] = Object.entries(parsed)[0];
				return { dataIndex: f.dataIndex, filterType: f.filterType, constrT: key, constV: value as string };
			});
		}
	}
	const [appliedFilters, setAppliedFilters] = React.useState<LocalStateFilterObject[]>(currentFilters);

	React.useEffect(() => setAppliedFilters(currentFilters), [filters]);

	const handleAddRow = () => {
		if (!Array.isArray(columns)) {
			setAppliedFilters([
				...appliedFilters,
				{ dataIndex: dataIndex ?? '', filterType: filterType ?? '', constrT: defaulFilter ?? '', constV: '' }
			]);
		} else setAppliedFilters([...appliedFilters, { dataIndex: '', filterType: '', constrT: '', constV: '' }]);
	};

	const handleRemoveRow = (i: number) => {
		const newList = [...appliedFilters];
		newList.splice(i, 1);
		setAppliedFilters(newList);
	};

	const handleConstVChange = (v: unknown, i: number) => {
		const list = [...appliedFilters];
		list[i].constV = v as string | string[];
		setAppliedFilters(list);
	};

	function handleConstraintChange(e: string, i: number) {
		const list = [...appliedFilters];
		list[i].constrT = e;
		setAppliedFilters(list);
	}

	function isDuplicate(entry: QueryFilter, arr: QueryFilter[]) {
		return arr.some(x => entry.dataIndex === x.dataIndex && entry.filterValue === x.filterValue);
	}

	function updateFilters(arr2: QueryFilter[], arr1?: QueryFilter[]) {
		const newArray: QueryFilter[] = [];
		for (const entry of arr2) {
			if (!isDuplicate(entry, newArray)) {
				newArray.push(entry);
			}
		}
		if (!Array.isArray(columns) && arr1) {
			onFiltersChange([...arr1.filter(f => f.dataIndex !== dataIndex), ...newArray]);
		} else {
			onFiltersChange(newArray);
		}
	}

	const handleApplyFilters = () => {
		const newFilters = appliedFilters
			.filter(filter => !strIsNullOrEmpty(filter.constrT) && !strIsNullOrEmpty(filter.dataIndex) && !strIsNullOrEmpty(filter.filterType))
			.map(f => {
				return {
					dataIndex: f.dataIndex,
					filterType: f.filterType,
					filterValue: JSON.stringify({ [f.constrT]: f.constV })
				};
			});
		// Preserve the other filters
		if (otherFilters) newFilters.push(...otherFilters);
		updateFilters(newFilters);
		onClose?.();
	};

	return (
		<Padded>
			<FlexColumnSpaced>
				<Typography variant="h6">Filtering</Typography>
				<FlexColumnSpaced style={{ padding: '0 1rem ' }}>
					{appliedFilters.map((singleRow, index) => {
						return (
							<FilterConditionItem
								key={index}
								index={index}
								filter={singleRow}
								totalFilterCount={appliedFilters.length}
								availableColumns={columns}
								operator={safeFilterOperatorsExt(singleRow.filterType)}
								filterOperatorsExt={safeFilterOperatorsExt}
								onAddNewFilter={handleAddRow}
								onValueChange={handleConstVChange}
								onRemove={handleRemoveRow}
								appliedFilters={appliedFilters}
								setAppliedFilters={setAppliedFilters}
								onModifierChange={handleConstraintChange}
							/>
						);
					})}
				</FlexColumnSpaced>
				<Divider />
				<FlexRowSpaced>
					<Button
						color="error"
						onClick={() => {
							onFiltersChange([]);
						}}
					>
						Clear all
					</Button>
					{showCloseButton ? (
						<Button variant="primary" style={{ marginLeft: 'auto' }} onClick={handleApplyFilters}>
							Apply filter
						</Button>
					) : null}
				</FlexRowSpaced>
			</FlexColumnSpaced>
		</Padded>
	);
}

// Keep the old FilterPopoverInner as an alias for backward compatibility
export const FilterPopoverInner = FilterEditor;
